/**
 * When to ask for a store rating (the in-app review sheet, @mp/adapters `review`). Pure rules over
 * PlayerState.prompts.review; the map hook that acts on them is features/review.
 *
 *  - The first ask comes at the player's 3rd level win ever or right after their first shop upgrade,
 *    whichever happens first. That moment is remembered as `pending`, so a later 3rd win does not
 *    relabel an ask the upgrade earned.
 *  - One more ask is allowed, at least 30 days after the first and 20 wins after it. That is the end:
 *    both stores cap their sheets (iOS three a year) and say nothing about whether one was shown, so
 *    the game cannot know what an ask did and does not keep trying.
 *  - Never during the first-session tutorial (Flow A), right after a loss (the last round lost), during a rescue offer, or in a session where a
 *    purchase failed: a player who just failed, or whose payment just went wrong, is the wrong one to
 *    ask. These only postpone; the trigger stays pending and the next calm map visit asks.
 *  - An ask is recorded only when a sheet was requested. Where the platform has none (web, Discord)
 *    nothing is asked or marked, so the player who installs the app later is not "already asked".
 *
 * Wins are counted here and not derived from `levels`: that map keeps one entry per level (best stars),
 * and a replayed win is a win.
 */
import type { PlayerState } from './state.js';

export const REVIEW = { afterWins: 3, repeatGapMs: 30 * 86_400_000, repeatWins: 20, maxAsks: 2 } as const;

/** What earned the ask (`review_prompt` props.trigger). */
export type ReviewTrigger = 'wins_3' | 'first_upgrade' | 'repeat';

export interface ReviewState {
  /** level wins ever, replays included */
  wins: number;
  /** the first ask's trigger, set when it is earned and kept until the ask is made */
  pending: Exclude<ReviewTrigger, 'repeat'> | null;
  /** sheets requested so far (at most REVIEW.maxAsks) */
  asks: number;
  /** when the last one was requested */
  lastAt: number | null;
  /** `wins` at that moment, for the repeat's 20 more */
  winsAtLast: number;
  /** how the most recent round ended (any mode); null before the first */
  lastResult: 'win' | 'loss' | null;
}

export function defaultReviewState(): ReviewState {
  return { wins: 0, pending: null, asks: 0, lastAt: null, winsAtLast: 0, lastResult: null };
}

/** A round ended. Only a level-mode win counts toward the wins. */
export function noteRound(s: PlayerState, o: { won: boolean; levelMode: boolean }): void {
  const r = s.prompts.review;
  r.lastResult = o.won ? 'win' : 'loss';
  if (!o.won || !o.levelMode) return;
  r.wins++;
  if (r.asks === 0 && r.pending === null && r.wins >= REVIEW.afterWins) r.pending = 'wins_3';
}

/** The player built a shop upgrade (any shop, any slot). */
export function noteUpgrade(s: PlayerState): void {
  const r = s.prompts.review;
  if (r.asks === 0 && r.pending === null) r.pending = 'first_upgrade';
}

/** What the player has earned an ask for, ignoring the moment (a loss, a rescue, a failed purchase). */
export function earnedTrigger(r: ReviewState, now: number): ReviewTrigger | null {
  if (r.asks === 0) return r.pending;
  if (r.asks >= REVIEW.maxAsks || r.lastAt === null) return null;
  return now - r.lastAt >= REVIEW.repeatGapMs && r.wins - r.winsAtLast >= REVIEW.repeatWins ? 'repeat' : null;
}

/** The moment, as the map sees it. */
export interface ReviewMoment {
  now: number;
  /** remote-config `reviewPrompt` */
  flag: boolean;
  /** the platform has a rating sheet (ReviewProvider.available) */
  available: boolean;
  /** a rescue offer is on screen */
  rescueOpen: boolean;
  /** a purchase failed earlier in this session */
  purchaseFailed: boolean;
  /** Flow A (first session) is still running: its free first upgrade lands mid-tutorial, before the player has seen the game */
  onboarding: boolean;
}

/** The trigger to ask for now, or null. */
export function reviewDue(s: PlayerState, m: ReviewMoment): ReviewTrigger | null {
  const r = s.prompts.review;
  if (!m.flag || !m.available || m.onboarding || m.rescueOpen || m.purchaseFailed || r.lastResult === 'loss') return null;
  return earnedTrigger(r, m.now);
}

/** A sheet was requested. */
export function markReviewAsked(s: PlayerState, now: number): void {
  const r = s.prompts.review;
  r.asks++;
  r.lastAt = now;
  r.winsAtLast = r.wins;
  r.pending = null;
}
